import { useState, useEffect, useRef, useCallback } from "react";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Lock,
  Camera,
  Save,
  Facebook,
  Instagram,
  Linkedin,
  Eye,
  EyeOff,
  GraduationCap,
  Droplet,
  Calendar,
  Shield,
} from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import {
  server_post_data,
  get_admin_profile,
  update_admin_profile,
  change_admin_password,
  APL_LINK,
} from "../serviceconnection/ServiceConnection";

const TABS = [
  { key: "personal", label: "Personal Info", icon: User },
  { key: "address", label: "Address", icon: MapPin },
  { key: "social", label: "Social Links", icon: Linkedin },
  { key: "password", label: "Change Password", icon: Lock },
];

const EMPTY_PWD = { old_password: "", new_password: "", confirm_password: "" };

// ── Defined OUTSIDE component to prevent remount on every render ──────────────
const InputField = ({
  label,
  placeholder,
  type = "text",
  icon: Icon,
  disabled,
  value,
  onChange,
}) => (
  <div className="space-y-1">
    <label className="text-xs font-semibold text-muted-foreground">
      {label}
    </label>
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background focus-within:border-accent ${disabled ? "opacity-60" : ""}`}
    >
      {Icon && <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      <input
        type={type}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1 bg-transparent outline-none text-sm text-foreground placeholder:text-muted-foreground disabled:cursor-not-allowed"
      />
    </div>
  </div>
);

const SelectField = ({ label, fieldKey, options, value, onChange }) => (
  <div className="space-y-1">
    <label className="text-xs font-semibold text-muted-foreground">
      {label}
    </label>
    <select
      value={value ?? ""}
      onChange={onChange}
      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
    >
      <option value="">Select...</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  </div>
);

export default function ProfileSettings() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [activeTab, setActiveTab] = useState("personal");
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [pwdForm, setPwdForm] = useState(EMPTY_PWD);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [showPwd, setShowPwd] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const fileRef = useRef();

  // ── Fetch ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const fd = new FormData();
        const res = await server_post_data(get_admin_profile, fd);
        const d = res.data?.data ?? {};
        setProfile(d);
        setForm({
          admin_name: d.admin_name ?? "",
          admin_mobile_no: d.admin_mobile_no ?? "",
          admin_other_no: d.admin_other_no ?? "",
          admin_email: d.admin_email ?? "",
          admin_gender: d.admin_gender ?? "",
          admin_blood_group: d.admin_blood_group ?? "",
          admin_education: d.admin_education ?? "",
          admin_dob_date: d.admin_dob_date ?? "",
          admin_city: d.admin_city ?? "",
          admin_state: d.admin_state ?? "",
          admin_country: d.admin_country ?? "",
          admin_manual_adrress: d.admin_manual_adrress ?? "",
          admin_zipcode: d.admin_zipcode ?? "",
          facebook_link: d.facebook_link ?? "",
          instragram_link: d.instragram_link ?? "",
          linkedin_link: d.linkedin_link ?? "",
        });
        setImagePreview(APL_LINK + (d.admin_image_url || null));
      } catch {
        showError("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const sf = useCallback(
    (key, val) => setForm((p) => ({ ...p, [key]: val })),
    [],
  );

  // ── Image pick ────────────────────────────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // ── Save Profile ──────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.admin_name) {
      showError("Name is required");
      return;
    }
    try {
      setSaveLoading(true);
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      if (imageFile) fd.append("admin_image_file", imageFile);

      const res = await server_post_data(update_admin_profile, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess("Profile updated successfully!");
      setImageFile(null);
    } catch {
      showError("Something went wrong");
    } finally {
      setSaveLoading(false);
    }
  };

  // ── Change Password ───────────────────────────────────────────────────
  const handlePasswordChange = async () => {
    if (
      !pwdForm.old_password ||
      !pwdForm.new_password ||
      !pwdForm.confirm_password
    ) {
      showError("All password fields are required");
      return;
    }
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      showError("New passwords do not match");
      return;
    }
    if (pwdForm.new_password.length < 6) {
      showError("Password must be at least 6 characters");
      return;
    }
    try {
      setPwdLoading(true);
      const fd = new FormData();
      fd.append("old_password", pwdForm.old_password);
      fd.append("new_password", pwdForm.new_password);

      const res = await server_post_data(change_admin_password, fd);
      if (res.data?.error) {
        showError(res.data?.message);
        return;
      }
      showSuccess("Password changed successfully!");
      setPwdForm(EMPTY_PWD);
    } catch {
      showError("Something went wrong");
    } finally {
      setPwdLoading(false);
    }
  };

  // ── Reusable input ────────────────────────────────────────────────────

  // ── Loading ───────────────────────────────────────────────────────────
  if (loading)
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
        Loading profile...
      </div>
    );

  // ── Initials avatar ───────────────────────────────────────────────────
  const initials = (profile?.admin_name ?? "A")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="max-w-[1000px] mx-auto space-y-5">
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Profile Settings</h1>
          <p className="page-subtitle">
            Manage your account information and preferences
          </p>
        </div>
      </div>

      {/* ── Profile Card ── */}
      <div className="cms-card p-5 flex flex-col sm:flex-row items-center sm:items-start gap-5">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="Profile"
              className="w-24 h-24 rounded-full object-cover border-2 border-border"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center text-2xl font-bold text-white"
              style={{ background: "hsl(var(--accent))" }}
            >
              {initials}
            </div>
          )}
          <button
            onClick={() => fileRef.current?.click()}
            className="absolute bottom-0 right-0 w-7 h-7 rounded-full flex items-center justify-center text-white shadow-md"
            style={{ background: "hsl(var(--accent))" }}
            title="Change photo"
          >
            <Camera className="w-3.5 h-3.5" />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageChange}
          />
        </div>

        {/* Info */}
        <div className="flex-1 text-center sm:text-left">
          <h2 className="text-lg font-bold text-foreground">
            {profile?.admin_name}
          </h2>
          <p className="text-sm text-muted-foreground">
            {profile?.designation_name}
          </p>
          <p className="text-sm text-muted-foreground mt-0.5">
            {profile?.admin_email}
          </p>
          <div className="flex flex-wrap gap-2 mt-2 justify-center sm:justify-start">
            {profile?.admin_city && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="w-3 h-3" /> {profile.admin_city}
              </span>
            )}
            {profile?.admin_join_date && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Calendar className="w-3 h-3" /> Joined{" "}
                {profile.admin_join_date}
              </span>
            )}
            <span
              className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{
                background: "hsl(var(--accent)/0.12)",
                color: "hsl(var(--accent))",
              }}
            >
              <Shield className="w-3 h-3" />{" "}
              {profile?.allow_access === "1" ? "Active" : "Restricted"}
            </span>
          </div>
        </div>
      </div>

      {/* ── Tabs + Content ── */}
      <div className="section-panel p-0">
        {/* Tab bar */}
        <div className="flex border-b border-border overflow-x-auto px-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap -mb-px ${
                activeTab === t.key
                  ? "border-accent text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="section-panel-body space-y-4">
          {/* ── Personal Info ── */}
          {activeTab === "personal" && (
            <>
              <InputField
                label="Full Name"
                placeholder="Your full name"
                icon={User}
                value={form.admin_name}
                onChange={(e) => sf("admin_name", e.target.value)}
              />
              <InputField
                label="Email"
                placeholder="Email address"
                icon={Mail}
                disabled
                value={form.admin_email}
                onChange={() => {}}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Mobile No"
                  placeholder="+91 98765 43210"
                  icon={Phone}
                  value={form.admin_mobile_no}
                  onChange={(e) => sf("admin_mobile_no", e.target.value)}
                />
                <InputField
                  label="Other No"
                  placeholder="Alternate number"
                  icon={Phone}
                  value={form.admin_other_no}
                  onChange={(e) => sf("admin_other_no", e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <SelectField
                  label="Gender"
                  options={["Male", "Female", "Other"]}
                  value={form.admin_gender}
                  onChange={(e) => sf("admin_gender", e.target.value)}
                />
                <SelectField
                  label="Blood Group"
                  options={["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"]}
                  value={form.admin_blood_group}
                  onChange={(e) => sf("admin_blood_group", e.target.value)}
                />
                <InputField
                  label="Date of Birth"
                  type="date"
                  icon={Calendar}
                  value={form.admin_dob_date}
                  onChange={(e) => sf("admin_dob_date", e.target.value)}
                />
              </div>
              <InputField
                label="Education"
                placeholder="e.g. B.Tech Computer Science"
                icon={GraduationCap}
                value={form.admin_education}
                onChange={(e) => sf("admin_education", e.target.value)}
              />
            </>
          )}

          {/* ── Address ── */}
          {activeTab === "address" && (
            <>
              <InputField
                label="Address"
                placeholder="Street / Building"
                icon={MapPin}
                value={form.admin_manual_adrress}
                onChange={(e) => sf("admin_manual_adrress", e.target.value)}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="City"
                  placeholder="City"
                  icon={MapPin}
                  value={form.admin_city}
                  onChange={(e) => sf("admin_city", e.target.value)}
                />
                <InputField
                  label="State"
                  placeholder="State"
                  icon={MapPin}
                  value={form.admin_state}
                  onChange={(e) => sf("admin_state", e.target.value)}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Country"
                  placeholder="Country"
                  icon={MapPin}
                  value={form.admin_country}
                  onChange={(e) => sf("admin_country", e.target.value)}
                />
                <InputField
                  label="Zipcode"
                  placeholder="Postal code"
                  icon={MapPin}
                  value={form.admin_zipcode}
                  onChange={(e) => sf("admin_zipcode", e.target.value)}
                />
              </div>
            </>
          )}

          {/* ── Social ── */}
          {activeTab === "social" && (
            <>
              <InputField
                label="LinkedIn"
                placeholder="https://linkedin.com/in/yourname"
                icon={Linkedin}
                value={form.linkedin_link}
                onChange={(e) => sf("linkedin_link", e.target.value)}
              />
              <InputField
                label="Facebook"
                placeholder="https://facebook.com/yourname"
                icon={Facebook}
                value={form.facebook_link}
                onChange={(e) => sf("facebook_link", e.target.value)}
              />
              <InputField
                label="Instagram"
                placeholder="https://instagram.com/yourname"
                icon={Instagram}
                value={form.instragram_link}
                onChange={(e) => sf("instragram_link", e.target.value)}
              />
            </>
          )}

          {/* ── Password ── */}
          {activeTab === "password" && (
            <div className="space-y-4 max-w-md">
              {[
                { label: "Current Password", key: "old_password", vis: "old" },
                { label: "New Password", key: "new_password", vis: "new" },
                {
                  label: "Confirm Password",
                  key: "confirm_password",
                  vis: "confirm",
                },
              ].map(({ label, key, vis }) => (
                <div key={key} className="space-y-1">
                  <label className="text-xs font-semibold text-muted-foreground">
                    {label}
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-background focus-within:border-accent">
                    <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <input
                      type={showPwd[vis] ? "text" : "password"}
                      value={pwdForm[key]}
                      onChange={(e) =>
                        setPwdForm((p) => ({ ...p, [key]: e.target.value }))
                      }
                      placeholder="••••••••"
                      className="flex-1 bg-transparent outline-none text-sm text-foreground"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowPwd((p) => ({ ...p, [vis]: !p[vis] }))
                      }
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {showPwd[vis] ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={handlePasswordChange}
                disabled={pwdLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: "hsl(var(--accent))" }}
              >
                <Lock className="w-4 h-4" />
                {pwdLoading ? "Updating..." : "Update Password"}
              </button>
            </div>
          )}

          {/* Save Button (not shown on password tab) */}
          {activeTab !== "password" && (
            <div className="flex justify-end pt-2 border-t border-border">
              <button
                onClick={handleSave}
                disabled={saveLoading}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: "hsl(var(--accent))" }}
              >
                <Save className="w-4 h-4" />
                {saveLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
